import React, { useEffect, useMemo, useState } from "react";
import { fmtRange } from "@/lib/enrol";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import { Users, ChevronDown, CheckSquare, Square, Save, GraduationCap } from "lucide-react";

const DAY_ORDER = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday","Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const dayRank = (d) => { const i = DAY_ORDER.indexOf(d || ""); return i < 0 ? 99 : i % 7; };

export default function BulkAssign() {
  const [instructors, setInstructors] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [instructorId, setInstructorId] = useState("");
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => {
    const [u, s] = await Promise.all([api.get("/admin/admin-users"), api.get("/admin/sessions")]);
    setInstructors((u.data || []).filter(x => x.role === "instructor" && x.active !== false));
    setSessions(s.data || []);
  };
  useEffect(() => { load(); }, []);

  // When picking an instructor, pre-check the sessions they currently teach
  useEffect(() => {
    const pre = {};
    sessions.forEach(s => { if (s.instructor_admin_id === instructorId) pre[s.id] = true; });
    setChecked(pre);
  }, [instructorId, sessions]);

  const currentInstructor = instructors.find(u => u.id === instructorId);
  const label = currentInstructor ? `Coach ${(currentInstructor.name || currentInstructor.instructor_name || currentInstructor.email || "").trim().split(/\s+/)[0].replace(/^\w/, c => c.toUpperCase())}` : "";

  const filtered = useMemo(() => sessions
    .filter(s => !q || `${s.batch_name} ${s.day} ${s.location_name} ${s.instructor}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a,b) => dayRank(a.day) - dayRank(b.day) || (a.start_time || "").localeCompare(b.start_time || "")),
    [sessions, q]);

  const toggle = (id) => setChecked(c => ({ ...c, [id]: !c[id] }));
  const allChecked = filtered.length && filtered.every(s => checked[s.id]);
  const toggleAll = () => {
    const next = { ...checked };
    if (allChecked) filtered.forEach(s => { next[s.id] = false; });
    else filtered.forEach(s => { next[s.id] = true; });
    setChecked(next);
  };

  const save = async () => {
    if (!instructorId) { toast.error("Pick an instructor first"); return; }
    const assign = Object.keys(checked).filter(k => checked[k]);
    // Sessions previously assigned to this instructor but now unchecked → clear
    const clear = sessions.filter(s => s.instructor_admin_id === instructorId && !checked[s.id]).map(s => s.id);
    setSaving(true);
    try {
      const r = await api.post("/admin/bulk-assign-instructor", {
        instructor_admin_id: instructorId, session_ids: assign, unassign_ids: clear,
      });
      toast.success(`Saved · assigned ${r.data.assigned} · cleared ${r.data.cleared}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1600px]">
      <div className="mb-6">
        <div className="eyebrow mb-2">Bulk assign</div>
        <h1 className="font-display text-5xl uppercase">Assign Instructor</h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-2xl">Pick an instructor, then tick every class you want them to teach. Currently-assigned classes are pre-ticked so you can just add or remove.</p>
      </div>

      <div className="flex gap-3 mb-6 items-end flex-wrap">
        <div className="min-w-[280px]">
          <label className="eyebrow block mb-1 flex items-center gap-2"><Users size={12}/>Instructor</label>
          <select value={instructorId} onChange={e => setInstructorId(e.target.value)} className="w-full bg-black border border-white/10 px-3 py-2 text-sm" data-testid="bulk-instructor-select">
            <option value="">— Pick instructor —</option>
            {instructors.map(u => <option key={u.id} value={u.id}>{`${u.instructor_name || u.name} · ${u.email}`}</option>)}
          </select>
        </div>
        {label && (
          <div className="text-xs text-zinc-400 pb-2 flex items-center gap-1">
            <GraduationCap size={12}/>Session label will be set to: <strong className="text-white">{label}</strong>
          </div>
        )}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter classes…" className="ml-auto bg-black border border-white/10 px-3 py-2 text-sm w-72" data-testid="bulk-search"/>
        <button onClick={save} disabled={!instructorId || saving} className="btn-red !py-2 !px-4 !text-xs flex items-center gap-1" data-testid="bulk-save">
          <Save size={12}/>{saving ? "Saving…" : "Save assignments"}
        </button>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-[10px]">
              <th className="px-3 py-3 text-left w-8">
                <button onClick={toggleAll} className="text-zinc-400 hover:text-white" data-testid="toggle-all">
                  {allChecked ? <CheckSquare size={16}/> : <Square size={16}/>}
                </button>
              </th>
              <th className="px-3 py-3 text-left">Day / time</th>
              <th className="px-3 py-3 text-left">Batch</th>
              <th className="px-3 py-3 text-left">Location</th>
              <th className="px-3 py-3 text-left">Currently taught by</th>
              <th className="px-3 py-3 text-left">Pay / class</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(s => {
              const isChecked = !!checked[s.id];
              const currentlyThisInstr = s.instructor_admin_id === instructorId;
              return (
                <tr key={s.id} onClick={() => toggle(s.id)} className={`border-b border-white/5 hover:bg-white/[0.02] cursor-pointer ${isChecked ? "bg-red-600/10" : ""}`} data-testid={`bulk-row-${s.id}`}>
                  <td className="px-3 py-3">
                    {isChecked ? <CheckSquare size={16} className="text-red-500"/> : <Square size={16} className="text-zinc-500"/>}
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-white">{s.day}</div>
                    <div className="text-[10px] text-zinc-500">{fmtRange(s.start_time, s.end_time)}</div>
                  </td>
                  <td className="px-3 py-3">{s.batch_name || "—"}</td>
                  <td className="px-3 py-3">{s.location_name || "—"}</td>
                  <td className="px-3 py-3">
                    <span className={currentlyThisInstr ? "text-emerald-400" : "text-zinc-400"}>{s.instructor || "unassigned"}</span>
                  </td>
                  <td className="px-3 py-3">${Number(s.pay_amount ?? 0).toFixed(2)}</td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-zinc-500">No sessions match.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
